import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { Chip, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";

interface TipSelectorProps {
  subtotal: number;
  currency: string;
  presetPercentages?: number[];
  value: number;
  disabled?: boolean;
  onChange: (amount: number) => void;
}

const DEFAULT_PRESETS = [10, 20, 25];

const tipFrom = (entered: string) => {
  const parsed = Number(entered);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const TipSelector: React.FC<TipSelectorProps> = ({
  subtotal,
  currency,
  presetPercentages,
  value,
  disabled = false,
  onChange,
}) => {
  const { t } = useTranslation();

  const presets = presetPercentages?.length
    ? presetPercentages
    : DEFAULT_PRESETS;

  // Round tips using the currency's minor unit.
  const amountFor = (percentage: number) => {
    const places =
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: (currency || "usd").toUpperCase(),
      }).resolvedOptions().maximumFractionDigits ?? 2;
    const scale = 10 ** places;

    return Math.round(((subtotal * percentage) / 100) * scale) / scale;
  };

  // A tip no preset produces can only have been typed, so it reopens as Other.
  const typedTip =
    value > 0 && !presets.some((percentage) => amountFor(percentage) === value);
  const [other, setOther] = useState(typedTip);
  const [custom, setCustom] = useState(typedTip ? String(value) : "");
  // The preset chosen, so its tip follows the amount it is a percentage of.
  const [percent, setPercent] = useState<number | null>(
    () =>
      (value > 0 &&
        presets.find((percentage) => amountFor(percentage) === value)) ||
      null,
  );

  useEffect(() => {
    if (percent == null || subtotal <= 0) return;
    const next = amountFor(percent);
    if (next !== value) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const choose = (percentage: number | null) => {
    setOther(false);
    setCustom("");
    setPercent(percentage);
    onChange(percentage == null ? 0 : amountFor(percentage));
  };

  const chooseOther = () => {
    setOther(true);
    setPercent(null);
    onChange(tipFrom(custom));
  };

  const chooseCustom = (entered: string) => {
    setCustom(entered);
    onChange(tipFrom(entered));
  };

  if (subtotal <= 0) {
    return null;
  }

  return (
    <Stack sx={{ gap: "var(--space-2)" }}>
      <Typography variant="body2">{t("payLink.AddATip")}</Typography>

      {/* A choice, not an action: the chosen tip takes the brand tint, so Pay now stays the one button to press. */}
      <Inline gap="var(--space-2)">
        <Chip
          tone={!other && percent == null ? "brand" : "default"}
          aria-pressed={!other && percent == null}
          disabled={disabled}
          onClick={() => choose(null)}
        >
          {t("No tip")}
        </Chip>

        {presets.map((percentage) => {
          const amount = amountFor(percentage);
          const chosen = !other && percent === percentage;

          return (
            <Chip
              key={percentage}
              tone={chosen ? "brand" : "default"}
              aria-pressed={chosen}
              disabled={disabled}
              onClick={() => choose(percentage)}
            >
              {`${percentage}% (${formatCashFull(amount, currency)})`}
            </Chip>
          );
        })}

        <Chip
          tone={other ? "brand" : "default"}
          aria-pressed={other}
          disabled={disabled}
          onClick={chooseOther}
        >
          {t("Other")}
        </Chip>
      </Inline>

      {other && (
        <TextField
          id="pay-link-tip-amount"
          type="number"
          label={t("Other amount")}
          value={custom}
          disabled={disabled}
          autoFocus
          inputProps={{ min: 0, step: "0.01" }}
          onChange={(event) => chooseCustom(event.target.value)}
        />
      )}
    </Stack>
  );
};

export default TipSelector;
